"use client";
/**
 * "Chart this ticker" — a request from a symbol LIST to the Advanced Chart
 * on the SAME PAGE.
 *
 * Why this exists rather than the symbol store: a list and a chart usually do
 * NOT share a symbol scope. `WatchlistPane` writes the scope it is mounted
 * under (the page's, e.g. `workspace:{slug}`), but every Advanced Chart cell
 * owns a scope of its own (`adv-pane:{paneId}[-cN]`) so two cells can hold two
 * tickers. The result was a watchlist that highlighted a row and charted
 * nothing — in the desk's OWN side panel as much as in a standalone pane,
 * since the side panel is a sibling of the desk, outside every cell's scope.
 *
 * Both watchlist surfaces therefore also post the pick here, and the ACTIVE
 * cell of the matching Advanced Chart adopts it. The symbol store write stays:
 * panes that genuinely share the page scope (Stock Detail, the scanner's
 * bidirectional wiring) still work off it.
 *
 * The request carries the page/desk scope. Keep-alive leaves desks from other
 * routes mounted; a pick on Outlook must not rewrite Charting, Dashboard,
 * Daily Brief, or a custom workspace. Hidden keep-alive desks also refuse the
 * event. An unscoped request is ignored — it used to hit every on-screen chart.
 *
 * Deliberately fire-and-forget: no store, no persistence. A request is a user
 * gesture, and a chart that mounts later must show its own saved ticker rather
 * than replay someone's last click.
 */
import { useCallback } from "react";
import { useSymbolScope, useSymbolStore } from "@/lib/stores/symbolStore";

export const CHART_SYMBOL_REQUEST_EVENT = "mpp:chart-symbol-request";

/** Keep-alive host in AdvancedDeskKeepAlive — hidden desks live under this. */
export const CHART_DESK_KEEPALIVE_BIN_ATTR = "data-adv-desk-keepalive-bin";

export type ChartSymbolRequestDetail = {
  /** Upper-cased ticker. */
  symbol: string;
  /** Page / desk `baseScope` that originated the pick. Required. */
  scope?: string;
};

/** True when the chart node is in the cross-route keep-alive bin, not on screen. */
export function chartOnScreen(el: Element | null | undefined): boolean {
  if (!el) return true;
  return el.closest(`[${CHART_DESK_KEEPALIVE_BIN_ATTR}]`) == null;
}

/**
 * Page identity for ticker-request matching.
 *
 * `workspace:` / `adv-pane:` use the full string (the colon is the page id).
 * Board cells `timing-brief:SPY` share a page with a watchlist on `timing-brief`.
 * Multi-cell desks use `${baseScope}-cN`.
 */
export function chartPageFamily(scope: string): string {
  const raw = (scope ?? "").trim();
  if (!raw) return "";
  const base = cellOf(raw) ?? raw;
  if (base.startsWith("workspace:") || base.startsWith("adv-pane:")) return base;
  const colon = base.indexOf(":");
  if (colon <= 0) return base;
  return base.slice(0, colon);
}

/**
 * A desk on page A must not take a pick from page B (keep-alive keeps both
 * mounted). Multi-cell scopes are `${baseScope}-cN`.
 */
export function chartAcceptsSymbolRequest(opts: {
  chartScope: string;
  requestScope?: string | null;
  onScreen: boolean;
}): boolean {
  if (!opts.onScreen) return false;
  const req = (opts.requestScope ?? "").trim();
  const chart = (opts.chartScope ?? "").trim();
  if (!req || !chart) return false;
  if (chart === req) return true;
  if (cellOf(chart) === req || cellOf(req) === chart) return true;
  const a = chartPageFamily(chart);
  const b = chartPageFamily(req);
  return a.length > 0 && a === b;
}

/** `"charting-c2"` → `"charting"`; `"timing-brief:SPY"` is left intact. */
function cellOf(scope: string): string | null {
  const m = /^(.*)-c\d+$/.exec(scope);
  return m?.[1] ?? null;
}

/** Ask the listening chart(s) to show `symbol`. No-op for a blank ticker. */
export function requestChartSymbol(symbol: string, scope?: string): void {
  if (typeof window === "undefined") return;
  const sym = String(symbol ?? "").trim().toUpperCase();
  if (!sym) return;
  const page = (scope ?? "").trim();
  if (!page) return;
  try {
    window.dispatchEvent(
      new CustomEvent(CHART_SYMBOL_REQUEST_EVENT, {
        detail: { symbol: sym, scope: page } satisfies ChartSymbolRequestDetail,
      }),
    );
  } catch {
    /* SSR / no CustomEvent */
  }
}

/** Subscribe a chart to those requests. Returns an unsubscribe fn. */
export function subscribeChartSymbolRequest(
  handler: (symbol: string, requestScope?: string) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const onEvt = (e: Event) => {
    const detail = (e as CustomEvent<ChartSymbolRequestDetail>).detail;
    const sym = detail?.symbol;
    if (typeof sym === "string" && sym) handler(sym, detail.scope);
  };
  window.addEventListener(CHART_SYMBOL_REQUEST_EVENT, onEvt);
  return () => window.removeEventListener(CHART_SYMBOL_REQUEST_EVENT, onEvt);
}

/** Page-scoped helper — Watchlist / The Read / heatmaps use this, not the raw event. */
export function useRequestChartSymbol(): (symbol: string) => void {
  const scope = useSymbolScope();
  return useCallback(
    (symbol: string) => {
      requestChartSymbol(symbol, scope);
    },
    [scope],
  );
}

/** Write the page store AND notify the page's Advanced Chart. */
export function useSetChartSymbol(): (symbol: string) => void {
  const setSymbol = useSymbolStore((s) => s.setSymbol);
  const requestChart = useRequestChartSymbol();
  return useCallback(
    (symbol: string) => {
      setSymbol(symbol);
      requestChart(symbol);
    },
    [setSymbol, requestChart],
  );
}
